<template>
  <div>
    <Heading v-if="title" level="h6" class="mb-3 !mt-0">{{ title }}</Heading>
    <div v-if="hasData">
      <div :style="{ height: `${height}px` }" class="relative">
        <canvas ref="chartCanvas" @click="handleClick"></canvas>
      </div>
      <!-- Custom HTML Legend -->
      <div v-if="showLegend && legendItems.length > 0" class="flex flex-wrap items-center justify-center gap-3 mt-4">
        <div 
          v-for="(item, index) in legendItems" 
          :key="index"
          class="flex items-center gap-2 text-sm cursor-pointer hover:opacity-75 transition-opacity"
          :class="{ 
            'cursor-default': item.isPlaceholder,
            'opacity-40 line-through': item.hidden
          }"
          @click="handleLegendClick(item, index)"
        >
          <span 
            class="inline-block w-3 h-3 rounded-full flex-shrink-0"
            :style="{ backgroundColor: item.fillStyle }"
          ></span>
          <span 
            class="text-gray-700 dark:text-gray-300"
            :class="{ 'text-gray-500 dark:text-gray-500': item.isPlaceholder }"
          >
            {{ item.text }}{{ item.value && !item.isPlaceholder ? ` (${item.value})` : '' }}
          </span>
          <!-- Tooltip for "+ X more..." -->
          <HLPopover
            v-if="item.isPlaceholder && item.hiddenItemsData"
            trigger="hover"
            placement="top"
            :width="300"
          >
            <template #trigger>
              <span class="inline-block w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help ml-1">
                <svg viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                  <path d="M5.255 5.786a.237.237 0 0 0 .241.247h.825c.138 0 .248-.113.266-.25.09-.656.54-1.134 1.342-1.134.686 0 1.314.343 1.314 1.168 0 .635-.374.927-.965 1.371-.673.489-1.206 1.06-1.168 1.987l.003.217a.25.25 0 0 0 .25.246h.811a.25.25 0 0 0 .25-.25v-.105c0-.718.273-.927 1.01-1.486.609-.463 1.244-.977 1.244-2.056 0-1.511-1.276-2.241-2.673-2.241-1.267 0-2.655.59-2.75 2.286zm1.557 5.763c0 .533.425.927 1.01.927.609 0 1.028-.394 1.028-.927 0-.552-.42-.94-1.029-.94-.584 0-1.009.388-1.009.94z"/>
                </svg>
              </span>
            </template>
            <div class="max-h-80 overflow-y-auto p-2">
              <div class="text-xs font-medium text-gray-900 dark:text-white mb-2 sticky top-0 bg-white dark:bg-gray-800 pb-1 z-10">
                All Issue Types ({{ getAllIssueTypesForPopover(item).length }})
              </div>
              <div class="space-y-1">
                <div 
                  v-for="(issueType, idx) in getAllIssueTypesForPopover(item)" 
                  :key="idx"
                  class="flex items-center justify-between gap-3 text-xs py-1 px-2 hover:bg-gray-50 dark:hover:bg-gray-700 rounded"
                >
                  <div class="flex items-center gap-2 flex-1 min-w-0">
                    <span 
                      class="inline-block w-2 h-2 rounded-full flex-shrink-0"
                      :style="{ backgroundColor: issueType.color }"
                    ></span>
                    <span class="text-gray-700 dark:text-gray-300 truncate" :title="issueType.label || issueType.text">
                      {{ issueType.label || issueType.text || 'Unknown' }}
                    </span>
                  </div>
                  <span class="text-gray-500 dark:text-gray-400 flex-shrink-0 font-medium">{{ issueType.value }}</span>
                </div>
              </div>
            </div>
          </HLPopover>
        </div>
      </div>
    </div>
    <EmptyState 
      v-else
      icon="chart" 
      :message="emptyMessage" 
      :height="`h-[${height}px]`"
      :show-action="showAction"
      :action-text="actionText"
      :action-icon="actionIcon"
      :action-variant="actionVariant"
      :action-size="actionSize"
      :action-disabled="actionDisabled"
      @action="handleAction"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue'
import { Chart, DoughnutController, ArcElement, Tooltip, Legend } from 'chart.js'
import { HLPopover } from '@platform-ui/highrise'
import Heading from '@/components/ui/Heading.vue'
import EmptyState from '@/components/ui/EmptyState.vue'
import { t } from '@/i18n/en-us'

Chart.register(DoughnutController, ArcElement, Tooltip, Legend)

const props = defineProps({
  data: {
    type: Array,
    required: true
  },
  height: {
    type: Number,
    default: 300
  },
  title: {
    type: String,
    default: ''
  },
  showLegend: {
    type: Boolean,
    default: true
  },
  maxLegendItems: {
    type: Number,
    default: null // null = show all, number = limit to top N + "X more..."
  },
  emptyMessage: {
    type: String,
    default: () => t('reports.noDataAvailable')
  },
  showAction: {
    type: Boolean,
    default: false
  },
  actionText: {
    type: String,
    default: ''
  },
  actionIcon: {
    type: String,
    default: 'plus'
  },
  actionVariant: {
    type: String,
    default: 'primary'
  },
  actionSize: {
    type: String,
    default: 'sm'
  },
  actionDisabled: {
    type: Boolean,
    default: false
  },
  tooltipLabel: {
    type: String,
    default: 'issues' // Default to 'issues' for backward compatibility
  }
})

const emit = defineEmits(['segment-click', 'action'])

const chartCanvas = ref(null)
let chartInstance = null
const legendItems = ref([])

// Computed
const hasData = computed(() => props.data && props.data.length > 0)

const createChart = () => {
  if (!chartCanvas.value || !props.data || props.data.length === 0) return

  const ctx = chartCanvas.value.getContext('2d')
  
  if (chartInstance) {
    chartInstance.destroy()
  }

  // Extract colors from data if available, otherwise use defaults
  const colors = props.data.map(d => d.color).filter(Boolean)
  const defaultColors = [
    'rgba(59, 130, 246, 0.8)',
    'rgba(16, 185, 129, 0.8)',
    'rgba(249, 115, 22, 0.8)',
    'rgba(239, 68, 68, 0.8)',
    'rgba(168, 85, 247, 0.8)',
    'rgba(236, 72, 153, 0.8)'
  ]
  const backgroundColors = colors.length > 0 ? colors : defaultColors

  chartInstance = new Chart(ctx, {
    type: 'doughnut',
    data: {
      labels: props.data.map(d => d.label),
      datasets: [{
        data: props.data.map(d => d.value),
        backgroundColor: backgroundColors
      }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false // We use custom HTML legend
        },
        tooltip: {
          backgroundColor: 'rgba(255, 255, 255, 0.98)',
          titleColor: '#111827',
          bodyColor: '#374151',
          borderColor: '#E5E7EB',
          borderWidth: 1,
          padding: 12,
          cornerRadius: 8,
          displayColors: true,
          boxPadding: 8,
          usePointStyle: true,
          titleSpacing: 4,
          bodySpacing: 6,
          callbacks: {
            title: (tooltipItems) => {
              const index = tooltipItems[0].dataIndex
              const dataItem = props.data[index]
              return dataItem.label
            },
            label: (context) => {
              const index = context.dataIndex
              const dataItem = props.data[index]
              const value = context.parsed
              const total = context.dataset.data.reduce((a, b) => a + b, 0)
              const percentage = ((value / total) * 100).toFixed(1)
              
              // If custom tooltip exists, use it (for "Others" group)
              if (dataItem.tooltip) {
                // Split by newlines for multi-line display
                return dataItem.tooltip.split('\n')
              }
              
              // Default tooltip with count and percentage
              if (dataItem.percentage) {
                // For Issue Types chart with percentage already calculated
                return [`${value} ${props.tooltipLabel} (${dataItem.percentage}%)`]
              }
              
              // Fallback with calculated percentage
              return [`${value} ${props.tooltipLabel} (${percentage}%)`]
            },
            labelColor: (context) => {
              const index = context.dataIndex
              const dataItem = props.data[index]
              return {
                borderColor: dataItem.color || '#6B7280',
                backgroundColor: dataItem.color || '#6B7280'
              }
            }
          },
          bodyFont: {
            size: 13,
            family: 'Inter, system-ui, -apple-system, sans-serif',
            weight: 'normal'
          },
          titleFont: {
            size: 14,
            weight: '600',
            family: 'Inter, system-ui, -apple-system, sans-serif'
          },
          // Allow tooltip to be taller for long lists
          yAlign: 'center',
          caretSize: 6,
          caretPadding: 8
        }
      },
      onClick: (event, elements) => {
        if (elements.length > 0) {
          const index = elements[0].index
          emit('segment-click', props.data[index])
        }
      }
    }
  })
  
  // Generate custom HTML legend items
  generateLegendItems()
}

const generateLegendItems = () => {
  if (!chartInstance || !props.showLegend) {
    legendItems.value = []
    return
  }
  
  const data = chartInstance.data
  if (!data.labels.length || !data.datasets.length) {
    legendItems.value = []
    return
  }
  
  const dataset = data.datasets[0]
  const allItems = data.labels.map((label, i) => {
    const item = {
      text: label,
      label: label, // Ensure label is available
      fillStyle: dataset.backgroundColor[i],
      strokeStyle: dataset.backgroundColor[i],
      hidden: false,
      index: i,
      value: dataset.data[i],
      color: dataset.backgroundColor[i]
    }
    return item
  })
  
  // If maxLegendItems is set and we have more items, show top N + "X more..."
  if (props.maxLegendItems && allItems.length > props.maxLegendItems) {
    const topItems = allItems.slice(0, props.maxLegendItems)
    const hiddenItems = allItems.slice(props.maxLegendItems)
    const hiddenCount = hiddenItems.length
    
    
    legendItems.value = [
      ...topItems,
      {
        text: `+ ${hiddenCount} more...`,
        fillStyle: 'rgba(156, 163, 175, 0.5)',
        strokeStyle: 'rgba(156, 163, 175, 0.5)',
        hidden: false,
        index: -1,
        isPlaceholder: true,
        hiddenItemsData: hiddenItems // Full data for popover
      }
    ]
  } else {
    legendItems.value = allItems
  }
}

const handleLegendClick = (item, index) => {
  if (!chartInstance || item.isPlaceholder) return
  
  const meta = chartInstance.getDatasetMeta(0)
  if (meta.data[item.index]) {
    meta.data[item.index].hidden = !meta.data[item.index].hidden
    chartInstance.update()
    // Update legend item hidden state
    legendItems.value[index].hidden = !legendItems.value[index].hidden
  }
}

const getAllIssueTypesForPopover = (item) => {
  if (!item || !item.isPlaceholder || !item.hiddenItemsData) {
    return []
  }
  
  // Get the top 5 visible items from legendItems (excluding the placeholder)
  const visibleItems = legendItems.value.filter(i => !i.isPlaceholder).slice(0, props.maxLegendItems || 5)
  
  // Combine visible + hidden items for complete list
  const allItems = [...visibleItems, ...item.hiddenItemsData]
  
  return allItems
}

const handleClick = (event) => {
  if (!chartInstance) return
  
  const elements = chartInstance.getElementsAtEventForMode(event, 'nearest', { intersect: true }, false)
  if (elements.length > 0) {
    const index = elements[0].index
    emit('segment-click', props.data[index])
  }
}

const handleAction = () => {
  emit('action')
}

onMounted(() => {
  createChart()
})

watch(() => props.data, async () => {
  // Wait for DOM to update (canvas to be re-mounted if it was removed)
  await nextTick()
  createChart()
}, { deep: true })
</script>

